/**
 * usePermissions — RBAC enforcement for the UI.
 *
 * Reads the logged-in user's resolved permission keys from the Redux auth store
 * (fetched ONCE at login and on app-load bootstrap — see authSlice). Screens pass
 * their JOB_NAMES value; the permission KEY for that job+capability comes from the
 * constants registry (constants/permissions), never built inline.
 *
 * A SUPER_ADMIN (ALL_PERMISSIONS) holds every key; a VIEWER holds only its set's
 * keys, so create/update/delete controls are hidden accordingly.
 */
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import { permissionKeyFor, JOB_PERMISSIONS, type PermissionAction } from '../constants/permissions';

interface PermissionCheckResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Reads permissions from the store and exposes a checker. `isLoaded` lets guards
 * show a spinner until the first fetch resolves.
 */
export function useMyPermissions() {
  const permissions = useAppSelector((state) => state.auth.permissions);
  const permissionsLoaded = useAppSelector((state) => state.auth.permissionsLoaded);

  const keys = useMemo(() => new Set<string>(permissions ?? []), [permissions]);

  const can = useCallback(
    (jobName: string, action: PermissionAction): boolean => {
      const permissionKey = permissionKeyFor(jobName, action);
      return permissionKey ? keys.has(permissionKey) : false;
    },
    [keys]
  );

  const has = useCallback((permissionKey: string): boolean => keys.has(permissionKey), [keys]);

  return { keys, can, has, isLoaded: permissionsLoaded, isLoading: !permissionsLoaded };
}

/**
 * Direct permission-name check — the preferred way to guard a button, screen or
 * action. Pass a named permission, e.g. useHasPermission(PERMISSIONS.APP_UPDATE).
 */
export function useHasPermission(permissionKey: string): boolean {
  return useMyPermissions().has(permissionKey);
}

/**
 * Object-style API (kept for existing callers).
 */
export function usePermissions() {
  const { keys, can, isLoaded } = useMyPermissions();

  const hasPermission = useCallback(
    (jobName: string, action: PermissionAction): PermissionCheckResult => ({
      allowed: can(jobName, action),
    }),
    [can]
  );

  const canCreate = useCallback((jobName: string) => can(jobName, 'create'), [can]);
  const canRead = useCallback((jobName: string) => can(jobName, 'read'), [can]);
  const canUpdate = useCallback((jobName: string) => can(jobName, 'update'), [can]);
  const canDelete = useCallback((jobName: string) => can(jobName, 'delete'), [can]);
  const hasAccessToJob = useCallback((jobName: string) => can(jobName, 'read'), [can]);

  const getJobPermissions = useCallback(
    (jobName: string): PermissionAction[] =>
      (['create', 'read', 'update', 'delete'] as PermissionAction[]).filter((action) =>
        can(jobName, action)
      ),
    [can]
  );

  const accessibleJobs = useMemo(
    () => Object.keys(JOB_PERMISSIONS).filter((jobName) => can(jobName, 'read')),
    [can]
  );

  return {
    hasPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    hasAccessToJob,
    getJobPermissions,
    accessibleJobs,
    permissionsData: Array.from(keys),
    isLoaded,
  };
}

// Convenience single-purpose hooks used throughout the screens.
export function useCanCreate(jobName: string): boolean {
  return useMyPermissions().can(jobName, 'create');
}
export function useCanRead(jobName: string): boolean {
  return useMyPermissions().can(jobName, 'read');
}
export function useCanUpdate(jobName: string): boolean {
  return useMyPermissions().can(jobName, 'update');
}
export function useCanDelete(jobName: string): boolean {
  return useMyPermissions().can(jobName, 'delete');
}
export function useHasJobAccess(jobName: string): boolean {
  return useMyPermissions().can(jobName, 'read');
}
